import React from 'react';
import { Card, CardContent } from "@shared/ui/card";

interface GuestPortalPreviewProps {
  bgMode: 'gradient' | 'photo';
  startColor: string;
  endColor: string;
  gradientDir: string;
  photoFile: File | null;
  headline: string;
  supportingText: string;
  showBanner: boolean;
  bannerMessage: string;
  bannerBg: string;
  bannerText: string;
}

const GuestPortalPreview: React.FC<GuestPortalPreviewProps> = ({
  bgMode,
  startColor,
  endColor,
  gradientDir,
  photoFile,
  headline,
  supportingText,
  showBanner,
  bannerMessage,
  bannerBg,
  bannerText
}) => {
  return (
    <Card className="shadow-sm border-gray-200">
      <CardContent className="p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Live Preview</h2>
        <p className="text-sm text-gray-500 mb-6">Preview of the guest portal as seen by unauthenticated visitors.</p>
        
        <div className="w-full bg-gray-50 border border-gray-200 rounded-lg overflow-hidden flex flex-col relative min-h-[450px]">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 py-3 border-b border-gray-200 bg-white shrink-0 z-10">
            GUEST PORTAL — PUBLIC VIEW
          </div>
          
          {/* Nav */}
          <div className="bg-white flex items-center justify-between px-4 py-3 border-b border-gray-100 z-10">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-600 rounded text-white font-bold flex items-center justify-center text-xs">S</div>
              <div className="font-bold text-sm tracking-tight text-gray-900">SMART360</div>
            </div>
            <div className="flex gap-4 text-xs font-semibold text-gray-600">
              <span>Home</span>
              <span>Pay Bills</span>
              <span>Services</span>
            </div>
            <div className="flex gap-2">
              <div className="px-3 py-1.5 border rounded text-xs font-semibold text-gray-600">Register</div>
              <div className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-semibold">Sign In</div>
            </div>
          </div>

          {/* Banner */}
          {showBanner && (
            <div className="w-full py-1.5 text-center text-[10px] font-medium z-10" style={{ backgroundColor: bannerBg, color: bannerText }}>
              {bannerMessage}
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel */}
            <div 
              className="w-1/2 p-6 flex flex-col text-white relative"
              style={bgMode === 'gradient' ? { background: `linear-gradient(${gradientDir}, ${startColor}, ${endColor})` } : (photoFile ? { backgroundImage: `url(${URL.createObjectURL(photoFile)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { backgroundColor: '#1a3a6f' })}
            >
              {bgMode === 'photo' && photoFile && <div className="absolute inset-0 bg-black/40" />}
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-8">
                  <div className="w-6 h-6 bg-white/20 rounded text-white font-bold flex items-center justify-center text-xs backdrop-blur-sm border border-white/20">S</div>
                  <div>
                    <div className="font-bold text-sm leading-tight">SMART360</div>
                    <div className="text-[8px] text-white/70 uppercase tracking-wider">UTILITY PORTAL</div>
                  </div>
                </div>
                
                <h2 className="text-2xl font-extrabold mb-2 leading-tight drop-shadow-sm">{headline}</h2>
                <p className="text-[11px] text-white/90 mb-8 max-w-[90%] font-medium leading-relaxed drop-shadow-sm">{supportingText}</p>
                
                <div className="space-y-3">
                  <div className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg p-3 text-xs font-semibold flex items-center gap-3 transition-colors cursor-pointer">
                    <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center text-[10px]">→</div>
                    Sign In — Access your account
                  </div>
                  <div className="w-full bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 rounded-lg p-3 text-xs font-semibold flex items-center gap-3 transition-colors cursor-pointer">
                    <div className="w-6 h-6 bg-white/10 rounded flex items-center justify-center text-[10px]">+</div>
                    Create Account
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel */}
            <div className="w-1/2 bg-[#f8f9fa] p-4 flex flex-col justify-center">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Quick Services</h3>
              <p className="text-[10px] text-gray-500 mb-4">No account needed — get started right away</p>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm cursor-pointer hover:border-blue-300">
                  <div className="w-6 h-6 mx-auto bg-blue-50 text-blue-600 rounded flex items-center justify-center mb-1">
                    <span className="text-[10px]">💳</span>
                  </div>
                  <div className="text-[10px] font-bold text-gray-900">Pay Bill</div>
                  <div className="text-[8px] text-gray-500">Pay outstanding bills</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm cursor-pointer hover:border-orange-300">
                  <div className="w-6 h-6 mx-auto bg-orange-50 text-orange-600 rounded flex items-center justify-center mb-1">
                    <span className="text-[10px]">🔧</span>
                  </div>
                  <div className="text-[10px] font-bold text-gray-900">Request Service</div>
                  <div className="text-[8px] text-gray-500">Raise a repair request</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm cursor-pointer hover:border-green-300">
                  <div className="w-6 h-6 mx-auto bg-green-50 text-green-600 rounded flex items-center justify-center mb-1">
                    <span className="text-[10px]">📍</span>
                  </div>
                  <div className="text-[10px] font-bold text-gray-900">New Connection</div>
                  <div className="text-[8px] text-gray-500">Apply for connection</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm cursor-pointer hover:border-teal-300">
                  <div className="w-6 h-6 mx-auto bg-teal-50 text-teal-600 rounded flex items-center justify-center mb-1">
                    <span className="text-[10px]">🔍</span>
                  </div>
                  <div className="text-[10px] font-bold text-gray-900">App Status</div>
                  <div className="text-[8px] text-gray-500">Track application</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm cursor-pointer hover:border-purple-300">
                  <div className="w-6 h-6 mx-auto bg-purple-50 text-purple-600 rounded flex items-center justify-center mb-1">
                    <span className="text-[10px]">❓</span>
                  </div>
                  <div className="text-[10px] font-bold text-gray-900">View FAQs</div>
                  <div className="text-[8px] text-gray-500">Common questions</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-center shadow-sm cursor-pointer hover:border-red-300">
                  <div className="w-6 h-6 mx-auto bg-red-50 text-red-600 rounded flex items-center justify-center mb-1">
                    <span className="text-[10px]">🏢</span>
                  </div>
                  <div className="text-[10px] font-bold text-gray-900">Nearby Centers</div>
                  <div className="text-[8px] text-gray-500">Find office locations</div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </CardContent>
    </Card>
  );
};

export default GuestPortalPreview;
